/**
 * "Hướng dẫn kết nối Twenty" (docs/specs/reporting-api.md §12 card "Cách nối với Twenty", §13.3):
 * the address to fetch from, the header, the connection document to download and the Twenty 2.44
 * recipe (two workflows per kind of data, no code), with what to paste into each step.
 */

import { Check, Copy, Download } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { api } from '../../../api';
import { Callout, Field, Section } from '../../../components/blocks';
import { WriteError } from '../../../components/Write';
import { ALL_DATASETS, resolveBaseUrl, sampleResponse, TWENTY_STEPS, twentyRecipe, twentySchedule, TWENTY_OBJECTS } from '../../../lib/reportingApi';
import { useApi } from '../../../lib/useApi';
import { selectClass } from '../../operator/shared';
import { useScopeLabels } from './dialogs';

/** A value to paste into Twenty: wraps on a narrow screen (an address is never cut), with a copy button. */
function CopyBlock({ value }: { value: string }) {
  const { t } = useTranslation('reporting');
  const [copied, setCopied] = useState(false);
  return (
    <span className="flex min-w-0 items-start gap-1">
      <code className="min-w-0 rounded bg-muted px-1.5 py-0.5 text-xs break-all">{value}</code>
      <button
        type="button"
        className="inline-flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
        onClick={() =>
          void navigator.clipboard?.writeText(value).then(
            () => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1500);
            },
            () => undefined,
          )
        }
        aria-label={copied ? t('copied') : `${t('copy')} ${value}`}
      >
        {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      </button>
    </span>
  );
}

function Code({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid min-w-0 gap-0.5 text-xs">
      <span className="font-medium text-muted-foreground">{label}</span>
      <CopyBlock value={value} />
    </div>
  );
}

export function TwentyGuide() {
  const { t } = useTranslation('reporting');
  const labels = useScopeLabels();
  const doc = useApi((signal) => api.getReportingOpenApi({ signal }), []);
  const [dataset, setDataset] = useState('customers');
  const [downloadError, setDownloadError] = useState<unknown>(null);
  const server = doc.status === 'success' ? doc.data.servers?.[0]?.url : undefined;
  const base = resolveBaseUrl(server, window.location.origin);
  const step = t('guide.fetchAddress');
  const r = twentyRecipe(base, dataset, step);
  const label = labels.dataset(dataset);

  const save = (value: unknown, name: string) => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const download = () => {
    setDownloadError(null);
    api.getReportingOpenApi().then(
      (d) => save(d, 'tino-reporting-openapi.json'),
      (e: unknown) => setDownloadError(e),
    );
  };

  // The sample answer of the fetching step for the kind of data picked below (its own fields).
  const downloadSample = () => {
    setDownloadError(null);
    api.getReportingOpenApi().then(
      (d) => {
        const sample = sampleResponse(d, dataset);
        if (sample === null) setDownloadError(new Error(`no sample answer for ${dataset}`));
        else save(sample, `tino-reporting-${dataset}-sample.json`);
      },
      (e: unknown) => setDownloadError(e),
    );
  };

  return (
    <Section id="twenty" title={t('guide.title')} description={t('guide.description')}>
      <div className="grid min-w-0 gap-5 [&>*]:min-w-0" data-twenty-guide>
        <div className="grid gap-3 sm:grid-cols-2 [&>*]:min-w-0">
          <div className="grid gap-1 text-sm">
            <span className="text-xs font-medium text-muted-foreground">{t('guide.base')}</span>
            <span data-base-url className="min-w-0">
              <CopyBlock value={base} />
            </span>
          </div>
          <div className="grid gap-1 text-sm">
            <span className="text-xs font-medium text-muted-foreground">
              {t('guide.header')} · {t('guide.headerValue')}
            </span>
            <span className="flex flex-wrap items-center gap-2">
              <CopyBlock value={r.header} />
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{r.headerValue}</code>
            </span>
            <span className="text-xs text-muted-foreground">{t('guide.headerValueHint')}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" size="sm" variant="outline" onClick={download} data-download-openapi>
            <Download className="size-4" aria-hidden="true" />
            {t('guide.download')}
          </Button>
          <span className="text-xs text-muted-foreground">{t('guide.downloadHint')}</span>
        </div>
        {downloadError ? (
          <Callout tone="crit" title={t('guide.downloadFailed')}>
            <WriteError error={downloadError} />
          </Callout>
        ) : null}

        <div className="grid gap-3 [&>*]:min-w-0">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h3 className="text-sm font-semibold">{t('guide.stepsTitle')}</h3>
            <Field label={t('guide.dataset')} className="w-full sm:w-56">
              <select className={selectClass} value={dataset} onChange={(e) => setDataset(e.target.value)} data-field="guide-dataset">
                {ALL_DATASETS.map((d) => (
                  <option key={d} value={d}>
                    {labels.dataset(d)}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <ol className="grid list-decimal gap-3 pl-5 text-sm marker:text-muted-foreground [&>li]:min-w-0">
            <li>{t('guide.steps.model', { object: r.object })}</li>
            <li>{t('guide.steps.schedule', { label, trigger: TWENTY_STEPS.schedule, cron: r.schedule })}</li>
            <li className="space-y-1">
              <p>{t('guide.steps.fetch', { http: TWENTY_STEPS.http, step, header: r.header })}</p>
              <Code label={t('guide.fetchAddress')} value={r.fetch} />
              <div className="flex flex-wrap items-center gap-3 pt-1">
                <Button type="button" size="sm" variant="outline" onClick={downloadSample} data-download-sample={dataset}>
                  <Download className="size-4" aria-hidden="true" />
                  {t('guide.sample', { label })}
                </Button>
                <span className="text-xs text-muted-foreground">{t('guide.sampleHint', { step, label })}</span>
              </div>
            </li>
            <li className="space-y-1">
              <p>{t('guide.steps.loop', { iterator: TWENTY_STEPS.iterator, upsert: TWENTY_STEPS.upsert, object: r.object })}</p>
              <div className="flex flex-wrap gap-4">
                <Code label={t('guide.rows')} value={r.items} />
                <Code label="id" value={r.matchId} />
              </div>
            </li>
            <li className="space-y-1">
              <p>{t('guide.steps.ack', { http: TWENTY_STEPS.http })}</p>
              <Code label={t('guide.ackAddress')} value={r.ack} />
              <Code label={t('guide.ackBody')} value={r.ackBody} />
            </li>
            <li className="space-y-1">
              <p>{t('guide.steps.more', { ifElse: TWENTY_STEPS.ifElse, label, onCall: TWENTY_STEPS.webhook })}</p>
              <Code label={t('guide.hasMore')} value={r.hasMore} />
            </li>
          </ol>
          <Callout tone="warn">{t('guide.steps.never')}</Callout>
        </div>

        <div className="grid gap-2 [&>*]:min-w-0">
          <h3 className="text-sm font-semibold">{t('guide.scheduleTitle')}</h3>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">{t('guide.scheduleCol.data')}</th>
                  <th className="px-3 py-2">{t('guide.scheduleCol.cron')}</th>
                  <th className="px-3 py-2">{t('guide.scheduleCol.object')}</th>
                </tr>
              </thead>
              <tbody>
                {ALL_DATASETS.map((d) => (
                  <tr key={d} className="border-t">
                    <td className="px-3 py-1.5">{labels.dataset(d)}</td>
                    <td className="px-3 py-1.5">
                      <code className="text-xs">{twentySchedule(d)}</code>
                    </td>
                    <td className="px-3 py-1.5">
                      <code className="text-xs">{TWENTY_OBJECTS[d]}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid gap-2">
          <h3 className="text-sm font-semibold">{t('guide.notesTitle')}</h3>
          <ul className="grid list-disc gap-1 pl-5 text-sm [&>li]:min-w-0">
            <li>{t('guide.notes.money')}</li>
            <li>{t('guide.notes.months')}</li>
            <li>{t('guide.notes.firstLoad')}</li>
            <li className="space-y-1">
              <p>{t('guide.notes.test')}</p>
              <Code label={t('guide.testAddress')} value={r.me} />
            </li>
          </ul>
        </div>
      </div>
    </Section>
  );
}
